<template>
  <section>
    <div ref="cw" class="d-block">
      <canvas
        v-show="renderAs === ''"
        ref="c"
        :width="canvasWidth"
        :height="canvasHeight"
        :style="canvasStyles"
        aria-labelledby="canvas-description"
        role="img"
      >
        <p id="canvas-description" class="sr-only">Interactive Personalization Canvas</p>
      </canvas>
    </div>
  </section>
</template>

<script>
import { fabric } from 'fabric';
import FontFaceObserver from 'fontfaceobserver';

export default {
  name: 'CustomDesign',
  props: {
    title: {
      type: String,
      default: '',
      required: false,
    },
    width: {
      type: [Number, String],
      default: null,
      required: false,
    },
    height: {
      type: [Number, String],
      default: null,
      required: false,
    },
    baseTagSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: true,
    },
    designSvgImgPath: {
      type: [String, Boolean],
      default: null,
      required: false,
    },
    canvasBGColor: {
      type: String,
      default: 'transparent',
      required: false,
    },
    boundaryPadding: {
      type: [String, Number],
      default: 0,
      required: false,
    },
    text1: {
      type: Object,
      default: null,
      required: false,
    },
    text2: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    text3: {
      type: [Object, Boolean],
      default: null,
      required: false,
    },
    selectable: {
      type: Boolean,
      default: false,
      required: false,
    },
    scale: {
      type: [String, Number],
      default: 0.6,
      required: false,
    },
    exportable: {
      type: Boolean,
      default: false,
      required: false,
    },
    value: {
      type: Object,
      default: null,
      required: false,
    },
    preview: {
      type: Boolean,
      default: false,
      required: false,
    },
    renderAs: {
      type: String,
      default: '',
      required: false,
    },
    canvasStyles: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      canvas: new fabric.StaticCanvas(),
      exportCanvas: null,
      canvasWidth: 0,
      canvasHeight: 0,
      baseTagRingRadius: 38,
      baseTagRadius: 208.6044724409,
      baseTagSvgImg: null,
      designSvgImg: null,
      windowWidth: window.innerWidth,
      debugMode: false,
      designerMode: !!this.value?.designerMode || false,
    };
  },
  computed: {
    computedProperty() {
      return this.text1, this.text2, this.text3, this.baseTagSvgImgPath, this.designSvgImgPath, this.debugMode, Date.now();
    },
    designerModeOverrideConfig: {
      get() {
        return this.value;
      },
      set(config) {
        this.$emit('input', config);
      },
    },
  },
  watch: {
    computedProperty() {
      this.$nextTick(() => {
        this.loadCanvas();
      });
    },
  },
  created() {
    window.addEventListener('resize', this.onWindowResize);
  },
  mounted() {
    this.loadCanvas();
  },
  destroyed() {
    window.removeEventListener('resize', this.onWindowResize);
    this.canvas?.dispose();
  },
  methods: {
    loadCanvas() {
      // Set SVG Images
      this.baseTagSvgImg = this.baseTagSvgImgPath || require('@/assets/images/dyot/SilverTagBase.svg');
      this.designSvgImg = this.designSvgImgPath ? this.designSvgImgPath : null;

      // Add Prototype Methods
      this.addCustomPrototypeMethods();

      // Init
      this.canvas = new fabric.StaticCanvas(this.$refs.c);

      // Set Background Color
      this.canvas.backgroundColor = this.canvasBGColor;

      // Set dimensions from parent (cw: canvas-wrapper)
      this.canvasWidth = Number(this.width) || this.$refs.cw.clientWidth;
      this.canvasHeight = Number(this.height) || this.$refs.cw.clientHeight || this.canvasWidth;
      this.canvas.setHeight(this.canvasHeight);
      this.canvas.setWidth(this.canvasWidth);

      // Clear canvas
      this.clearCanvas();

      // Add Title to canvas
      if (this.title) this.addCanvasTitle(this.title);

      // Draw on Canvas
      this.drawOnCanvas(this.baseTagSvgImg, this.designSvgImg);

      // Render Canvas
      this.renderCanvas();

      // Load export canvas if exportable
      if (this.exportable) this.loadExportCanvas();
    },
    loadExportCanvas() {
      this.exportCanvas = new fabric.StaticCanvas().setHeight(415).setWidth(415);
      this.drawOnCanvas(this.baseTagSvgImg, this.designSvgImg, true);
      this.renderCanvas(true);
      return this.exportCanvas;
    },
    drawOnCanvas(baseSVG, designSVG = null, forExport = false) {
      fabric.loadSVGFromURL(baseSVG, (bObjs, bOpts) =>
        fabric.loadSVGFromURL(designSVG || '', (dObjs, dOpts) => {
          // Base & Design Layer Group
          const BG = new fabric.Group([new fabric.util.groupSVGElements(bObjs, bOpts)], {
              originY: 'center',
              originX: 'center',
              top: 0,
              left: 0,
            }),
            DG = new fabric.Group(dObjs ? [new fabric.util.groupSVGElements(dObjs, dOpts)] : []);
          DG.setPositionByOrigin(new fabric.Point(DG.left, DG.top + fabric.BASE_TAG_RING_RADIUS), 'center', 'center');
          // Add Text Layer
          if (this.text1) {
            fabric.DESIGNER_MODE = this.designerMode;
            const TG = new fabric.Group([]);
            if (this.text1 && this.text1.text) {
              this.FabricText1 = TG.addTextLine(this.text1.text, this.text1);
              if (this.text2 && this.text2.text) {
                this.FabricText2 = TG.addTextLine(this.text2.text, this.text2);
                if (this.text3 && this.text3.text) {
                  this.FabricText3 = TG.addTextLine(this.text3.text, this.text3);
                }
              }
            }
            DG.addWithUpdate(TG);
            if (this.debugMode) {
              // Draw Text Boundary
              TG.addWithUpdate(
                new fabric.Rect({
                  top: this.FabricText1?.offsetY,
                  originY: 'bottom',
                  originX: 'center',
                  width: this.FabricText1?.width,
                  height: this.FabricText1?.height,
                  fill: 'transparent',
                  stroke: 'blue',
                })
              );
              TG.addWithUpdate(
                new fabric.Rect({
                  top: fabric.BASE_TAG_RING_RADIUS,
                  originY: 'center',
                  originX: 'center',
                  width: fabric.BASE_TAG_RADIUS * 2,
                  height: 0,
                  fill: 'black',
                  stroke: 'black',
                })
              );
              TG.addWithUpdate(
                new fabric.Rect({
                  top: this.FabricText2?.offsetY,
                  originY: 'top',
                  originX: 'center',
                  width: this.FabricText2?.width,
                  height: this.FabricText2?.height,
                  fill: 'transparent',
                  stroke: 'blue',
                })
              );
            }
          }
          if (!forExport && this.exportable && this.designerMode && !this.preview) {
            // Draw Circular Safety Boundary on Base Layer Group / Tag - Circle
            BG.addWithUpdate(
              new fabric.Circle({
                radius: 0.92 * fabric.BASE_TAG_RADIUS,
                top: fabric.BASE_TAG_RING_RADIUS,
                originY: 'center',
                originX: 'center',
                stroke: 'red',
                strokeDashArray: [10, 10],
                fill: 'transparent',
                excludeFromExport: true,
              })
            );
          }
          // Debug Mode
          if (this.debugMode) {
            // Draw Rectangular Boundary on Base Layer Group
            BG.addWithUpdate(
              new fabric.Rect({
                originY: 'center',
                originX: 'center',
                width: BG.width,
                height: BG.height,
                fill: 'transparent',
                stroke: 'red',
              })
            );
            // Draw Circular Boundary on Base Layer Group / Tag - Circle
            DG.addWithUpdate(
              new fabric.Circle({
                radius: fabric.BASE_TAG_RADIUS,
                top: fabric.BASE_TAG_RING_RADIUS,
                originY: 'center',
                originX: 'center',
                stroke: 'red',
                fill: 'transparent',
              })
            );
          }
          if (!forExport) {
            BG.addWithUpdate(DG);
            // Scale to fit in available space
            const availableCanvasHeight =
                (Number(this.canvasHeight) - Number(this.boundaryPadding) - (this.canvasTitleHeight || 0)) * (Number(this.scale) || 1),
              availableCanvasWidth = (Number(this.canvasWidth) - Number(this.boundaryPadding)) * (Number(this.scale) || 1);
            BG.scaleToCanvas(availableCanvasWidth, availableCanvasHeight);
            // Add to canvas
            this.canvas.centerObject(BG).add(BG);
          } else {
            this.exportCanvas.backgroundColor = 'transparent';
            // Add to export canvas;
            this.exportCanvas.centerObject(DG).add(DG);
          }
        })
      );
    },
    // Custom ProtoTypes
    addCustomPrototypeMethods() {
      fabric.Object.prototype.objectCaching = false;
      fabric.Object.prototype.statefullCache = false;
      fabric.Object.prototype.noScaleCache = true;

      // TAG CONSTANTS
      fabric.DEBUG = this.debugMode;
      fabric.DESIGNER_MODE = this.designerMode;
      fabric.BASE_TAG_RADIUS = this.baseTagRadius;
      fabric.BASE_TAG_RING_RADIUS = this.baseTagRingRadius;

      // Fabric Static Canvas
      fabric.StaticCanvas.prototype.renderAll = function () {
        const canvasToDrawOn = this?.contextContainer;
        if (!canvasToDrawOn) return;
        this.renderCanvas(canvasToDrawOn, this._objects);
        return this;
      };
      fabric.StaticCanvas.prototype.clearContext = function (ctx) {
        if (!ctx) return;
        fabric.util.clearFabricFontCache();
        ctx.clearRect(0, 0, this.width, this.height);
        return this;
      };

      // Fabric Text
      fabric.Text.prototype.setCustomFontFamily = function (fontFamily) {
        if (!fontFamily) return this;
        new FontFaceObserver(fontFamily).load().then(() => (this.fontFamily = fontFamily));
        this.fontFamily = fontFamily;
        return this;
      };
      fabric.Text.prototype.setMaxCharacters = function (maxChars, callback = null) {
        this.maxCharacters = maxChars;
        if (this.text.length >= maxChars) {
          if (callback && callback instanceof Function) callback(this.text.length, maxChars);
          else {
            this.text = this.text.substr(0, maxChars);
          }
        }
        return this;
      };
      fabric.Text.prototype.setFontSize = function (fontSize, minFontSizeCallback = null, maxFontSizeCallback = null) {
        if (!fontSize) return this;
        if (fontSize > this.maxFontSize)
          if (maxFontSizeCallback instanceof Function) maxFontSizeCallback(this);
          else this.fontSize = this.maxFontSize;
        else if (fontSize < this.minFontSize)
          if (minFontSizeCallback instanceof Function) minFontSizeCallback(this);
          else this.fontSize = this.minFontSize;
        else this.fontSize = fontSize;
        this.initDimensions();
        this.setCoords();
        this.setPosition(this.top, this.left);
        return this;
      };
      fabric.Text.prototype.setPosition = function (newTop = null, newLeft = null) {
        if ((newTop == null && newLeft == null) || (newTop === this.top && newLeft === this.left)) return this;
        if (!isNaN(newTop)) this.offsetY = this.top = Number(newTop);
        if (!isNaN(newLeft)) this.offsetX = this.left = Number(newLeft);
        this.initDimensions();
        this.setCoords();
        return this;
      };
      fabric.Text.prototype.addTextLabel = function (labelContent, labelOptions, lineOptions) {
        const LabelText = new fabric.Text(labelContent, {
          left: fabric.BASE_TAG_RADIUS * 2 + Number(labelOptions?.offsetX),
          top: Number(this.offsetY) + Number(labelOptions?.offsetY),
          originX: 'center',
          originY: 'center',
          fill: '#02294f',
          objectCaching: false,
          ...labelOptions,
        });
        const LabelLine = new fabric.Line(
          [
            fabric.BASE_TAG_RADIUS / lineOptions?.offsetX1 || 1,
            Number(this.offsetY) + lineOptions?.offsetY1 || 0,
            LabelText.left - LabelText.width / 2 - lineOptions?.offsetX2 || 0,
            LabelText.top + lineOptions?.offsetY2 || 0,
          ],
          {
            strokeWidth: 5,
            stroke: '#02294f',
            originX: 'center',
            originY: 'center',
            objectCaching: false,
            ...lineOptions,
          }
        );
        this.group.addWithUpdate(new fabric.Group([LabelText, LabelLine]), {
          originX: 'center',
          originY: 'center',
          objectCaching: false,
        });
        return this;
      };
      fabric.Text.prototype.autoScaleFonts = function (boundaryPadding = 0) {
        const maxWidthOfTextChord = (debugMode = false) => {
            // Perpendicular Leg
            const distFromCenterY = Math.abs(
                this.getCenterPoint().distanceFrom(this.group?.getCenterPoint().setY(fabric.BASE_TAG_RING_RADIUS))
              ),
              // Hypotenuse
              h = fabric.BASE_TAG_RADIUS,
              // Calc Chord Leg (* 2 chord length / max width)
              mw = Math.sqrt(Math.pow(h, 2) - Math.pow(Math.min(distFromCenterY), 2)) * 2 - boundaryPadding * 2;
            if (fabric.DEBUG && debugMode) {
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y - 0.47 * this.fontSize,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  height: 1,
                  fill: 'transparent',
                  stroke: 'red',
                })
              );
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  fill: 'transparent',
                  stroke: 'yellow',
                })
              );
              this.group?.addWithUpdate(
                new fabric.Rect({
                  top: this.getCenterPoint().y + 0.37 * this.fontSize,
                  left: this.getCenterPoint().x,
                  originY: 'center',
                  originX: 'center',
                  width: mw,
                  height: 1,
                  fill: 'transparent',
                  stroke: 'red',
                })
              );
            }
            return mw;
          },
          calcScaleFactor = (debugMode = false) => {
            const _maxWidth = maxWidthOfTextChord(debugMode),
              _scaleFactorW = _maxWidth / this.measureLine(0).width,
              _scaleFactorMaxW = (_maxWidth + boundaryPadding) / this.measureLine(0).width,
              _scaleFactorH = (fabric.BASE_TAG_RADIUS * Number(this.heightScaleFactor || 1)) / this.__lineHeights[0];
            return {
              maxWidth: _maxWidth,
              scaleFactorW: _scaleFactorW,
              scaleFactorH: _scaleFactorH,
              scaleFactorMaxW: _scaleFactorMaxW,
              scaleFactor: Math.min(_scaleFactorH || 1, _scaleFactorW || 1),
            };
          },
          calcScaleAndSetFontSize = () => {
            const calculatedScaleFactor = calcScaleFactor();
            this.setFontSize(!fabric.DESIGNER_MODE ? this.fontSize * calculatedScaleFactor.scaleFactor : this.fontSize);

            return this.fontSize;
          };

        calcScaleAndSetFontSize();
        this.boundingRect = new fabric.Rect({
          top: this.getCenterPoint().y,
          left: this.getCenterPoint().x,
          originY: 'center',
          originX: 'center',
          width: this.measureLine(0).width,
          height: this.fontSize,
          fill: 'transparent',
          stroke: this.fill || 'black',
        });
        if (fabric.DEBUG) {
          this.group?.addWithUpdate(this.boundingRect);
          this.group?.addWithUpdate(
            new fabric.Rect({
              top: this.getCenterPoint().y,
              left: this.getCenterPoint().x,
              originY: 'center',
              originX: 'center',
              width: maxWidthOfTextChord(true),
              height: this.fontSize,
              fill: 'transparent',
              stroke: 'green',
            })
          );
        }
        calcScaleAndSetFontSize();
        return this;
      };

      // Fabric Group
      fabric.Group.prototype.addTextLine = function (
        textContent,
        {
          color,
          offsetX,
          offsetY,
          options,
          fontFamily,
          lineOptions,
          fontSize,
          minFontSize,
          maxFontSize,
          labelOptions,
          maxCharacters,
          boundaryPadding,
          heightScaleFactor,
        },
        beforeAddCallback = null
      ) {
        const FabricText = new fabric.Text(textContent, {
          offsetY: fabric.DESIGNER_MODE ? Number(offsetY) : fabric.BASE_TAG_RING_RADIUS + Number(offsetY),
          top: fabric.DESIGNER_MODE ? Number(offsetY) : fabric.BASE_TAG_RING_RADIUS + Number(offsetY),
          offsetX: fabric.DESIGNER_MODE ? Number(offsetX) : -Number(offsetX),
          left: fabric.DESIGNER_MODE ? Number(offsetX) : -Number(offsetX),
          originY: 'center',
          originX: 'center',
          fill: color,
          group: this,
          textAlign: 'center',
          boundaryPadding,
          heightScaleFactor,
          ...options,
        })
          .setCustomFontFamily(fontFamily)
          .setMaxCharacters(maxCharacters);
        if (fontSize) FabricText.fontSize = fontSize;
        FabricText.initDimensions();
        FabricText.setCoords();
        FabricText.minFontSize = minFontSize;
        FabricText.maxFontSize = maxFontSize;
        if (!fabric.DESIGNER_MODE)
          FabricText.autoScaleFonts(
            Number(offsetX) >= 0 ? Number(boundaryPadding) + Number(offsetX) : Number(boundaryPadding) - Number(offsetX)
          );
        if (beforeAddCallback) beforeAddCallback(this);
        this.addWithUpdate(FabricText);
        if (lineOptions && labelOptions) FabricText.addTextLabel(labelOptions.text, labelOptions, lineOptions);
        return FabricText;
      };
      fabric.Group.prototype.scaleToCanvas = function (canvasWidth, canvasHeight) {
        const scaleFactor = canvasWidth / canvasHeight;
        let counter = 0;
        while (this.getBoundingRect(true, true).width > canvasWidth || this.getBoundingRect(true, true).height > canvasHeight) {
          if (counter >= 3) break;
          if (this.getBoundingRect(true, true).width > canvasWidth) {
            canvasWidth = this.scaleToWidth(canvasWidth, true).width;
            canvasHeight = canvasWidth / scaleFactor;
          } else {
            canvasHeight = this.scaleToHeight(canvasHeight, true).height;
            canvasWidth = canvasHeight * scaleFactor;
          }
          counter++;
        }
        return this;
      };
    },
    // Adds title in the canvas
    addCanvasTitle(title) {
      const Title = new fabric.Text(title, {
        fontSize: 17,
        top: this.canvas.height - 30,
        fill: '#6b6b6b',
        fontWeight: 500,
        selectable: false,
        excludeFromExport: true,
        fontFamily: 'lato',
      }).setCoords();
      this.canvas.centerObjectH(Title).add(Title);
      this.canvasTitleHeight = Title.height + 30;
    },
    activateDesignerMode(activate = true) {
      this.designerMode = fabric.DESIGNER_MODE = activate;
      const _loadCurrentConfig = {
        text1: null,
        text2: null,
        designerMode: false,
      };
      if (this.designerMode === true && fabric.DESIGNER_MODE === true) {
        _loadCurrentConfig.designerMode = true;
      }
      if (this.FabricText1) {
        _loadCurrentConfig.text1 = {
          fontSize: Number(this.FabricText1.fontSize),
          offsetY: Number(this.FabricText1.offsetY),
          offsetX: Number(this.FabricText1.offsetX),
          options: { originY: this.FabricText1?.originY || 'center' },
        };
      }
      if (this.FabricText2) {
        _loadCurrentConfig.text2 = {
          fontSize: Number(this.FabricText2.fontSize),
          offsetY: Number(this.FabricText2.offsetY),
          offsetX: Number(this.FabricText2.offsetX),
        };
      }
      return _loadCurrentConfig;
    },
    reduceFontSize(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.fontSize = this.FabricText1.setFontSize(_loadCurrentConfig.text1.fontSize - 5)?.fontSize;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.fontSize = this.FabricText2.setFontSize(_loadCurrentConfig.text2.fontSize - 5)?.fontSize;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    increaseFontSize(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.fontSize = this.FabricText1.setFontSize(_loadCurrentConfig.text1.fontSize + 5)?.fontSize;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.fontSize = this.FabricText2.setFontSize(_loadCurrentConfig.text2.fontSize + 5)?.fontSize;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveUp(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.offsetY = this.FabricText1.setPosition(
          _loadCurrentConfig.text1.offsetY - 10,
          _loadCurrentConfig.text1.offsetX
        )?.offsetY;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.offsetY = this.FabricText2.setPosition(
          _loadCurrentConfig.text2.offsetY - 10,
          _loadCurrentConfig.text2.offsetX
        )?.offsetY;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveDown(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.offsetY = this.FabricText1.setPosition(
          _loadCurrentConfig.text1.offsetY + 10,
          _loadCurrentConfig.text1.offsetX
        )?.offsetY;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.offsetY = this.FabricText2.setPosition(
          _loadCurrentConfig.text2.offsetY + 10,
          _loadCurrentConfig.text2.offsetX
        )?.offsetY;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveLeft(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.offsetX = this.FabricText1.setPosition(
          _loadCurrentConfig.text1.offsetY,
          _loadCurrentConfig.text1.offsetX - 10
        )?.offsetX;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.offsetX = this.FabricText2.setPosition(
          _loadCurrentConfig.text2.offsetY,
          _loadCurrentConfig.text2.offsetX - 10
        )?.offsetX;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    moveRight(lineNo) {
      const _loadCurrentConfig = this.activateDesignerMode();
      if (lineNo == 1 && this.FabricText1) {
        _loadCurrentConfig.text1.offsetX = this.FabricText1.setPosition(
          _loadCurrentConfig.text1.offsetY,
          _loadCurrentConfig.text1.offsetX + 10
        )?.offsetX;
      }
      if (lineNo == 2 && this.FabricText2) {
        _loadCurrentConfig.text2.offsetX = this.FabricText2.setPosition(
          _loadCurrentConfig.text2.offsetY,
          _loadCurrentConfig.text2.offsetX + 10
        )?.offsetX;
      }
      this.designerModeOverrideConfig = _loadCurrentConfig;
    },
    clearCanvas() {
      const CanvasJSON = this.canvas?.toJSON();
      this.canvas?.clear();
      this.canvas?.loadFromJSON(CanvasJSON);
    },
    renderCanvas(forExport = false) {
      forExport ? this.exportCanvas?.renderAll()?.calcOffset() : this.canvas?.renderAll();
    },
    exportCanvasLaserFront() {
      if (!this.exportCanvas) this.loadExportCanvas();
      return this.exportCanvas?.toDataURL('png');
    },
    // This would export front of the tag for display tag under pet tags (PO Account) and basket (Order App)
    exportCanvasPreview() {
      const previewCanvas = new fabric.Canvas().setHeight(500).setWidth(500),
        canvasJSON = this.canvas.toJSON();
      canvasJSON.background = 'transparent';
      previewCanvas.loadFromJSON(canvasJSON, previewCanvas.renderAll.bind(previewCanvas), function (o, object) {
        previewCanvas.centerObject(object.scaleToWidth(500).scaleToHeight(500));
      });
      const previewCanvasPNG = previewCanvas?.toDataURL('png');
      previewCanvas.clear().dispose().remove();
      return previewCanvasPNG;
    },
    debounce(func, timeout = 300) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.debounce(() => {
        this.loadCanvas();
      }, 500);
    },
  },
};
</script>
